import { useEffect, useRef, useState } from "react";

// Recharts paints SVG presentation *attributes* (fill="…"), and `var(--chart-1)`
// does not resolve there -- only through CSS. So the Recharts primitives need the
// token's resolved value, read off the document and re-read whenever the theme
// changes. The class-based approach in chart-utils.ts stays correct for the
// hand-written SVG charts; this is its counterpart for Recharts.

/** The categorical palette's fixed slot count (--chart-1 … --chart-6). */
export const CHART_SLOTS = 6;

export interface ChartTheme {
  /** --chart-1 … --chart-6, in slot order. Assign in order, never cycled. */
  series: string[];
  grid: string;
  axis: string;
  /** Card surface -- the gap drawn between adjacent fills. */
  surface: string;
  text: string;
  textMuted: string;
  /** Resting fill of an empty track / tooltip cursor. */
  track: string;
}

// tokens.css light values, used before the document can be read (SSR, jsdom).
const FALLBACK: ChartTheme = {
  series: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"],
  grid: "#e6e8ec",
  axis: "#4a5262",
  surface: "#ffffff",
  text: "#121826",
  textMuted: "#6b7280",
  track: "#f1f2f4",
};

function readVar(style: CSSStyleDeclaration, name: string, fallback: string): string {
  const v = style.getPropertyValue(name).trim();
  return v === "" ? fallback : v;
}

function readTheme(): ChartTheme {
  if (typeof window === "undefined" || typeof getComputedStyle !== "function") return FALLBACK;
  const s = getComputedStyle(document.documentElement);
  return {
    series: FALLBACK.series.map((fb, i) => readVar(s, `--chart-${i + 1}`, fb)),
    grid: readVar(s, "--chart-grid", FALLBACK.grid),
    axis: readVar(s, "--chart-axis", FALLBACK.axis),
    surface: readVar(s, "--color-surface-0", FALLBACK.surface),
    text: readVar(s, "--color-text", FALLBACK.text),
    textMuted: readVar(s, "--color-text-muted", FALLBACK.textMuted),
    track: readVar(s, "--color-surface-100", FALLBACK.track),
  };
}

/**
 * The resolved chart palette for the active theme. Follows the light/dark switch
 * the same way `SpecularButton` does: the `data-theme` attribute plus the OS
 * preference for "system".
 */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(readTheme);

  useEffect(() => {
    const sync = () => setTheme(readTheme());
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
    const scheme = window.matchMedia("(prefers-color-scheme: dark)");
    scheme.addEventListener("change", sync);

    return () => {
      observer.disconnect();
      scheme.removeEventListener("change", sync);
    };
  }, []);

  return theme;
}

/** True when the viewer asked for reduced motion; charts then skip their entry animation. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Counts from 0 up to `target` once, on first mount, so a headline number arrives
 * with the marks it describes rather than snapping in ahead of them. Later changes
 * (a filter narrowing the data) land immediately -- only the first run animates.
 */
export function useCountUp(target: number, duration = 800, enabled = true): number {
  const [value, setValue] = useState(enabled ? 0 : target);
  const ran = useRef(false);

  useEffect(() => {
    if (!enabled || ran.current) { setValue(target); return; }
    ran.current = true;

    let raf = 0;
    const started = performance.now();
    const tick = (now: number) => {
      // Elapsed-time progress against a real duration, eased out to match the
      // chart's own entry animation.
      const p = Math.min(1, (now - started) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled]);

  return value;
}

/** Entry animation for a chart: on first mount only, and never under reduced motion. */
export function useEntryAnimation(): { active: boolean; onEnd: () => void; duration: number } {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(true);
  return {
    active: active && !reduced,
    onEnd: () => setActive(false),
    duration: 800,
  };
}

/**
 * Resolve a single CSS custom property to its current value. Call it during a
 * render that also uses `useChartTheme()`, so a theme switch re-resolves it.
 */
export function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined" || typeof getComputedStyle !== "function") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v === "" ? fallback : v;
}

/**
 * Colour slot for an entity, assigned from a name-sorted key list rather than the
 * row's position in the (count-sorted) data. Colour then follows the entity, so
 * filtering the chart down never repaints the rows that survive.
 */
export function stableSlots(rows: { id: string | null; name: string }[]): Map<string, number> {
  const ordered = [...rows].sort((a, b) => a.name.localeCompare(b.name));
  return new Map(ordered.map((r, i) => [r.id ?? r.name, i % CHART_SLOTS]));
}

/** Share of a total, as a rounded percentage string. */
export function pct(value: number, total: number): string {
  if (total <= 0) return "0%";
  const p = (value / total) * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`;
}
